<template>
    <Tooltip class="h-full min-w-full" :is-teleported="true">
        <template #trigger="tooltipProps">
            <button
                class="border border-transparent focus-visible:outline-hidden flex place-items-center place-content-center size-full"
                :class="[
                    backgroundColorClass,
                    borderColorClass,
                    textColorClass,
                    {
                        'cursor-not-allowed': props.status.type === 'Loading' || isDisabled,
                        'cursor-pointer': props.status.type === 'Create' || props.status.type === 'Retry',
                    },
                ]"
                :aria-disabled="props.status.type === 'Loading' || isDisabled"
                type="button"
                v-bind="tooltipProps"
                @mousedown.prevent
                @click="clickButton"
            >
                <PlusIcon v-if="props.status.type === 'Create' || isDisabled" class="size-4" />
                <PendingIcon v-if="props.status.type === 'Loading'" class="size-4 animate-spin" />
                <RefreshIcon v-if="props.status.type === 'Retry'" class="size-4" />
            </button>
        </template>

        <template #header>
            {{ props.tooltipHeader }}
        </template>

        <template #text>
            <div v-if="props.status.type === 'Create'" class="min-w-max">{{ $t('click-to-create-allocation') }}</div>
            <div v-if="props.status.type === 'Loading'" class="min-w-max">{{ $t('creating-allocation') }}</div>
            <div v-if="props.status.type === 'Retry'" class="min-w-max">{{ $t('creating-allocation-failed') }}</div>
            <div v-if="isDisabled" class="min-w-max">{{ disabledTooltipText }}</div>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import PendingIcon from '../../Icon/PendingIcon.vue';
import PlusIcon from '../../Icon/PlusIcon.vue';
import RefreshIcon from '../../Icon/RefreshIcon.vue';
import Tooltip from '../../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../../Tooltip/injectionKeys';

export type FullHeightCreateButtonStatus =
    | { type: 'Create' }
    | { type: 'Loading' }
    | { type: 'Retry' }
    | { type: 'Disabled'; tooltipText: string };

type FullHeightCreateButtonProps = {
    status: FullHeightCreateButtonStatus;
    tooltipHeader: string;
    isInHoveredColumn: boolean;
    isInHoveredRow: boolean;
};

const props = defineProps<FullHeightCreateButtonProps>();

const emit = defineEmits<{
    (e: 'click'): void;
}>();

const isDisabled = computed(() => props.status.type === 'Disabled');
const disabledTooltipText = computed(() => (props.status.type === 'Disabled' ? props.status.tooltipText : ''));

const backgroundColorClass = computed(() => {
    switch (props.status.type) {
        case 'Create':
            return 'hover:bg-emerald-50 focus-visible:bg-emerald-50 active:bg-emerald-100';

        case 'Loading':
            return '';

        case 'Retry':
            return 'hover:bg-red-50 focus-visible:bg-red-50 active:bg-red-100';

        case 'Disabled':
            return '';
    }
});

const borderColorClass = computed(() => {
    switch (props.status.type) {
        case 'Create':
            return `
                hover:border hover:border-dashed hover:border-emerald-700
                focus-visible:border focus-visible:border-dashed focus-visible:border-emerald-700 active:border active:border-solid active:border-emerald-700
            `;

        case 'Loading':
            return '';

        case 'Retry':
            return `
                hover:border hover:border-dashed hover:border-red-700
                focus-visible:border focus-visible:border-dashed focus-visible:border-red-700 active:border active:border-solid active:border-red-700
            `;

        case 'Disabled':
            return '';
    }
});

const isExactCellHovered = computed(() => props.isInHoveredColumn && props.isInHoveredRow);
const textColorClass = computed(() => {
    switch (props.status.type) {
        case 'Create':
            if (isExactCellHovered.value) {
                return 'text-white hover:text-emerald-700 focus-visible:text-emerald-700 active:text-emerald-700';
            }
            return 'text-gray-400 hover:text-emerald-700 focus-visible:text-emerald-700 active:text-emerald-700';

        case 'Loading':
            if (isExactCellHovered.value) {
                return 'text-white';
            }
            return 'text-gray-700';

        case 'Retry':
            if (isExactCellHovered.value) {
                return 'text-white hover:text-red-700 focus-visible:text-red-700 active:text-red-700';
            }
            return 'text-red-700';

        case 'Disabled':
            if (isExactCellHovered.value) {
                return 'text-gray-100';
            }
            return 'text-gray-300';
    }
});

function clickButton() {
    if (props.status.type !== 'Loading' && !isDisabled.value) {
        triggerHideTooltip();
        emit('click');
    }
}

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>
